import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { ZUnsafeDataMap } from "src/business/attribute/ZAttrValue";
import { loadSpecification, ZSpecification } from "src/business/specification";
import { makeAttrMetaMap } from "src/business/specification/makeAttrMetaMap";
import { makeRowEvaluate } from "src/business/specification/rowEvaluate";
import {
  loadValueCatalog,
  loadValueCatalogCard,
  makeSafeValuesCatalogData,
  updateValueCatalogCard,
} from "src/business/valueCatalog/apiValueCatalog";
import { ZValueCatalog } from "src/business/valueCatalog/ZValueCatalog";
import { ZValueCatalogCard } from "src/business/valueCatalog/ZValueCatalogCard";
import { delay } from "src/common/delay";
import { RemoteData } from "src/common/RemoteData";
import { MFormStore } from "src/components/MForm/MFormStore";
import { PageUrl } from "src/routes/PageUrl";

import { makeValueCatalogRecInfo } from "../ValueCatalogRecForm/makeValueCatalogRecInfo";

export type ValueCatalogRecEditInfo = {
  card: ZValueCatalogCard;
  catalog: ZValueCatalog;
  spec: ZSpecification;
};

export const valueCatalogRecEditStore = makeAutoObservable({
  async init(valueCatalogCardId: string) {
    try {
      this.setData({ status: "wait" });
      const card = await loadValueCatalogCard(valueCatalogCardId);
      const catalog = await loadValueCatalog(card.catalogValueId);
      const spec = await loadSpecification("values", catalog.schema.id);

      const { meta, data } = makeValueCatalogRecInfo(spec, card);
      this.form.setMeta(meta);

      const result = { card, catalog, spec };
      this.setData({ status: "ready", result });

      await delay(10);
      this.form.setInitialValues(data);
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },
  form: new MFormStore(),
  data: {} as RemoteData<ValueCatalogRecEditInfo>,
  setData(newData: RemoteData<ValueCatalogRecEditInfo>) {
    this.data = newData;
  },
  get title(): string {
    // TODO: Это заглушка. Потому что сейчас нет правил для выбора названия карточки
    const { data } = this;
    if (data.status !== "ready") return "...";
    const { spec, card } = data.result;
    const attr = spec.definition?.attributes[0];
    if (!attr) return `Запись ${card.displayId}`;
    const rowEval = makeRowEvaluate(`{{${attr.name}}}`, [spec]);
    const safeCard = {
      ...card,
      data: makeSafeValuesCatalogData(
        card.data,
        spec.name,
        makeAttrMetaMap(spec),
      ),
    };
    return rowEval(safeCard);
  },
  get crumbs() {
    const result: BreadcrumbItem[] = [
      { title: "Каталоги значений", page: PageUrl.managementValueCatalogs },
    ];
    const { data, title } = this;
    if (data.status === "ready") {
      result.push({
        title: data.result.catalog.name,
        page: PageUrl.valueCatalog,
        params: { id: data.result.catalog.id },
      });
    }
    result.push({ title });
    return result;
  },
  async submit(dataMap: ZUnsafeDataMap): Promise<ZValueCatalogCard> {
    const { data } = this;
    if (data.status !== "ready") throw Error("Нет данных");
    const newCard = await updateValueCatalogCard(data.result.card.id, dataMap);
    this.setData({
      status: "ready",
      result: { ...data.result, card: newCard },
    });
    this.form.setInitialValues(newCard.data);
    return newCard;
  },
});
